import React, { useState } from "react";
import { useQuery, useMutation } from "@tanstack/react-query";
import { getRestaurantSlots, bookTable } from "../../../api/user/restaurants";
import toast from "react-hot-toast";
import { useLocationData } from "../../../context/locationContext";

// Simple chat bubble UI
const ChatBubble = ({ from, children }) => (
  <div className={`flex ${from === "bot" ? "justify-start" : "justify-end"} my-2`}>
    <div
      className={`px-4 py-2 rounded-lg  ${
        from === "bot"
          ? "bg-gray-200 text-black rounded-bl-none"
          : "bg-blue-700 text-white rounded-br-none"
      }`}
    >
      {children}
    </div>
  </div>
);

const ChatBookingFlow = ({ restaurantId}) => {
  const { userLocation } = useLocationData();

  // API: get slots
  const { data } = useQuery({
    queryKey: ["restaurant-slots", restaurantId],
    queryFn: () => getRestaurantSlots(restaurantId),
    enabled: !!restaurantId,
  });

  const slots = data?.data?.slots || [];

  const [step, setStep] = useState(1);
  const [messages, setMessages] = useState([
    { from: "bot", text: "👋 Hi! Let's book a table. First, select your booking date." },
  ]);

  const [selectedData, setSelectedData] = useState({
    bookingDate: "",
    mealType: "",
    slot: "",
    restaurantId,
    totalGuest: 1,
    foodType: "",
  });

  const bookTableMutation = useMutation({
    mutationFn: (payload) => bookTable(payload),
    onSuccess: () => {
      setMessages((prev) => [
        ...prev,
        { from: "bot", text: "✅ Your booking request has been placed successfully!" },
        { from: "bot", text: "We are now waiting for restaurant approval. You’ll be notified once confirmed." },
      ]);
      setStep(999); // finished
    },
    onError: (error) => {
      const msg = error?.response?.data?.message || "Failed to book table. Try again.";
      toast.error(msg);
    },
  });

  // Helpers
  const formatTime = (timeStr) => {
    if (!timeStr) return "";
    const [hourStr, minute] = timeStr.split(":");
    let hour = parseInt(hourStr, 10);
    const ampm = hour >= 12 ? "PM" : "AM";
    hour = hour % 12 || 12;
    return `${hour}:${minute} ${ampm}`;
  };

  // Step UI
  const renderStep = () => {
    switch (step) {
      case 1:
        return (
          <ChatBubble from="bot">
            <input
              type="date"
              className="border rounded px-2 py-1"
              min={new Date().toISOString().split("T")[0]}
              value={selectedData.bookingDate}
              onChange={(e) => {
                setSelectedData({ ...selectedData, bookingDate: e.target.value });
                setMessages((prev) => [...prev, { from: "user", text: e.target.value }]);
                setStep(2);
                setMessages((prev) => [...prev, { from: "bot", text: "Great! How many guests?" }]);
              }}
            />
          </ChatBubble>
        );

      case 2:
        return (
          <ChatBubble from="bot">
            <div className="flex gap-2 flex-wrap">
              {Array.from({ length: 5 }, (_, i) => i + 1).map((num) => (
                <button
                  key={num}
                  className="px-3 py-1 border rounded bg-white hover:bg-gray-100"
                  onClick={() => {
                    setSelectedData({ ...selectedData, totalGuest: num });
                    setMessages((prev) => [...prev, { from: "user", text: `${num} Guest(s)` }]);
                    setStep(3);
                    setMessages((prev) => [...prev, { from: "bot", text: "Choose a meal type:" }]);
                  }}
                >
                  {num}
                </button>
              ))}
            </div>
          </ChatBubble>
        );

      case 3:
        return (
          <ChatBubble from="bot">
            <div className="flex gap-2">
              {["breakfast", "lunch", "dinner"].map((meal) => (
                <button
                  key={meal}
                  className="px-3 py-1 border rounded bg-white hover:bg-gray-100 capitalize"
                  onClick={() => {
                    setSelectedData({ ...selectedData, mealType: meal });
                    setMessages((prev) => [...prev, { from: "user", text: meal }]);
                    setStep(4);
                    setMessages((prev) => [...prev, { from: "bot", text: "Veg, Non-Veg, or Both?" }]);
                  }}
                >
                  {meal}
                </button>
              ))}
            </div>
          </ChatBubble>
        );

      case 4:
        return (
          <ChatBubble from="bot">
            <div className="flex gap-2">
              {["veg", "non-veg", "both"].map((ftype) => (
                <button
                  key={ftype}
                  className="px-3 py-1 border rounded bg-white hover:bg-gray-100 capitalize"
                  onClick={() => {
                    setSelectedData({ ...selectedData, foodType: ftype });
                    setMessages((prev) => [...prev, { from: "user", text: ftype }]);
                    setStep(5);
                    setMessages((prev) => [...prev, { from: "bot", text: "Now pick a time slot:" }]);
                  }}
                >
                  {ftype}
                </button>
              ))}
            </div>
          </ChatBubble>
        );

      case 5:
        return (
          <ChatBubble from="bot">
            <div className="flex flex-wrap gap-2">
              {slots?.map((slot) => {
                const selectedDate = new Date(selectedData.bookingDate);
                const [hours, minutes] = slot.startTime.split(":");
                selectedDate.setHours(+hours, +minutes, 0, 0);
                const isPast = selectedDate < new Date();

                return (
                  <button
                    key={slot._id}
                    disabled={isPast}
                    className={`px-3 py-1 border rounded ${
                      isPast ? "bg-gray-200 text-gray-400" : "bg-white hover:bg-gray-100"
                    }`}
                    onClick={() => {
                      setSelectedData({ ...selectedData, slot: slot._id });
                      setMessages((prev) => [...prev, { from: "user", text: formatTime(slot.startTime) }]);
                      // Final booking call
                      bookTableMutation.mutate({ ...selectedData, slot: slot._id });
                    }}
                  >
                    {formatTime(slot.startTime)}
                  </button>
                );
              })}
            </div>
          </ChatBubble>
        );

      default:
        return null;
    }
  };

  return (
    <div className="w-full mx-auto rounded-lg bg-white">
      <div className="h-[500px] flex flex-col">
        {messages.map((msg, i) => (
          <ChatBubble key={i} from={msg.from}>
            {msg.text}
          </ChatBubble>
        ))}
        {renderStep()}
      </div>
    </div>
  );
};

export default ChatBookingFlow;
